import { isIframeMonitoringEnabled } from "./rumConstants";
import { trackMonitoringEvent, getCurrentProductId } from "./rumClient";

// Helper function to safely initialize RUM
const ensureRumInitialized = async (): Promise<void> => {
  try {
    // Only initialize in browser environment
    if (typeof window !== "undefined") {
      const rumClientModule = await import("./rumClient");
      if (typeof rumClientModule.rumInit === "function") {
        rumClientModule.rumInit(false); // Cookies disabled to prevent event deduplication
      }
    }
  } catch (error) {
    // Ignore initialization errors in test environment
    console.warn("RUM initialization skipped:", error);
  }
};

export interface IframeErrorDetails {
  message: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  error?: Error;
  stack?: string;
  timestamp: number;
  iframeUrl: string;
  userAgent: string;
}

export class IframeErrorMonitor {
  private iframeRef: HTMLIFrameElement | null = null;
  private errorListener: ((event: ErrorEvent) => void) | null = null;
  private unhandledRejectionListener: ((event: PromiseRejectionEvent) => void) | null = null;
  private isMonitoring = false;

  /**
   * Start monitoring iframe for JavaScript errors
   * Now enabled by X-Frame-Options: SAMEORIGIN and CSP frame-ancestors changes
   * Only enabled for Beta environment via feature flag
   */
  public startMonitoring(iframe: HTMLIFrameElement): void {
    // Check feature flag first
    if (!isIframeMonitoringEnabled()) {
      console.log("Iframe monitoring is disabled for this environment");
      return;
    }

    if (this.isMonitoring) {
      this.stopMonitoring();
    }

    // Ensure RUM is initialized before starting monitoring
    ensureRumInitialized();

    this.iframeRef = iframe;
    this.isMonitoring = true;

    // Wait for iframe to load before setting up monitoring
    iframe.addEventListener("load", () => {
      this.setupIframeErrorCapture();
    });

    // If iframe is already loaded, set up monitoring immediately
    if (iframe.contentDocument?.readyState === "complete") {
      this.setupIframeErrorCapture();
    }
  }

  /**
   * Set up error capture within the iframe content
   */
  private setupIframeErrorCapture(): void {
    if (!this.iframeRef?.contentWindow) {
      console.warn("Cannot access iframe content window");
      return;
    }

    try {
      const iframeWindow = this.iframeRef.contentWindow;
      const iframeDocument = this.iframeRef.contentDocument;

      if (!iframeWindow || !iframeDocument) {
        console.warn("Cannot access iframe window or document");
        return;
      }

      // Enhanced iframe error monitoring with same-origin access
      this.errorListener = (event: ErrorEvent) => {
        this.handleIframeError({
          message: event.message || "Unknown error",
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error,
          stack: event.error?.stack,
          timestamp: Date.now(),
          iframeUrl: this.iframeRef?.src || "unknown",
          userAgent: navigator.userAgent,
        });
      };

      // Capture unhandled promise rejections within iframe
      this.unhandledRejectionListener = (event: PromiseRejectionEvent) => {
        this.handleIframeError({
          message: `Unhandled Promise Rejection: ${event.reason}`,
          error: event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
          stack: event.reason instanceof Error ? event.reason.stack : undefined,
          timestamp: Date.now(),
          iframeUrl: this.iframeRef?.src || "unknown",
          userAgent: navigator.userAgent,
        });
      };

      // Add error listeners to iframe window
      iframeWindow.addEventListener("error", this.errorListener, true);
      iframeWindow.addEventListener("unhandledrejection", this.unhandledRejectionListener, true);

      this.setupXAPIMonitoring(iframeWindow);

      this.setupAssetLoadingMonitoring(iframeWindow);

      this.setupPerformanceMonitoring(iframeWindow);

      console.log("Enhanced iframe monitoring started");

      // Track successful monitoring setup
      trackMonitoringEvent("iframe_monitoring_enabled", {
        iframeUrl: this.iframeRef.src || "unknown",
        productId: getCurrentProductId(),
        timestamp: Date.now(),
      });
    } catch (error) {
      console.warn("Failed to set up iframe error monitoring:", error);

      // Track monitoring setup failure
      trackMonitoringEvent("iframe_monitoring_failed", {
        error: error instanceof Error ? error.message : String(error),
        iframeUrl: this.iframeRef?.src || "unknown",
        timestamp: Date.now(),
      });
    }
  }

  /**
   * Set up xAPI/LRS communication monitoring
   */
  private setupXAPIMonitoring(iframeWindow: Window): void {
    try {
      // Monitor fetch requests for LRS calls
      const originalFetch = iframeWindow.fetch;
      if (originalFetch) {
        iframeWindow.fetch = function (...args: Parameters<typeof fetch>) {
          const url = args[0];
          const urlString = typeof url === "string" ? url : (url as Request).url;
          const isLRSCall = urlString.includes("/lrs/") || urlString.includes("/xapi/");

          return originalFetch
            .apply(this, args)
            .then((response) => {
              if (isLRSCall && !response.ok) {
                const monitor = (window as any).__iframeErrorMonitor;
                if (monitor) {
                  trackMonitoringEvent("lrs_request_failed", {
                    url: urlString,
                    status: response.status,
                    statusText: response.statusText,
                    productId: getCurrentProductId(),
                    timestamp: Date.now(),
                  });
                }
              }
              return response;
            })
            .catch((error) => {
              if (isLRSCall) {
                const monitor = (window as any).__iframeErrorMonitor;
                if (monitor) {
                  trackMonitoringEvent("lrs_network_error", {
                    url: urlString,
                    error: error.message || "Network error",
                    productId: getCurrentProductId(),
                    timestamp: Date.now(),
                  });
                }
              }
              throw error;
            });
        };
      }

      // Store reference for cleanup
      (window as any).__iframeErrorMonitor = this;
    } catch (error) {
      console.warn("Failed to set up xAPI monitoring:", error);
    }
  }

  /**
   * Set up asset loading failure monitoring
   */
  private setupAssetLoadingMonitoring(iframeWindow: Window): void {
    try {
      // Monitor image loading failures
      iframeWindow.addEventListener(
        "error",
        (event) => {
          const target = event.target as HTMLElement;
          if (target && (target.tagName === "IMG" || target.tagName === "VIDEO" || target.tagName === "AUDIO")) {
            trackMonitoringEvent("asset_load_failed", {
              assetType: target.tagName.toLowerCase(),
              src: (target as any).src || "unknown",
              productId: getCurrentProductId(),
              timestamp: Date.now(),
            });
          }
        },
        true
      );
    } catch (error) {
      console.warn("Failed to set up asset loading monitoring:", error);
    }
  }

  /**
   * Set up performance monitoring
   */
  private setupPerformanceMonitoring(iframeWindow: Window): void {
    try {
      // Monitor course initialization time
      const startTime = Date.now();

      // Check for course player initialization
      const checkInitialization = () => {
        if (iframeWindow.document.readyState === "complete") {
          const loadTime = Date.now() - startTime;
          trackMonitoringEvent("course_performance", {
            name: "course_performance",
            loadTime: loadTime,
            productId: getCurrentProductId(),
            timestamp: Date.now(),
          });
        } else {
          setTimeout(checkInitialization, 100);
        }
      };

      setTimeout(checkInitialization, 100);
    } catch (error) {
      console.warn("Failed to set up performance monitoring:", error);
    }
  }

  /**
   * Handle captured iframe errors and send to CloudWatch RUM
   */
  private handleIframeError(errorDetails: IframeErrorDetails): void {
    console.error("Iframe JavaScript Error Captured:", errorDetails);

    // Send detailed error to CloudWatch RUM
    trackMonitoringEvent("iframe_javascript_error", {
      message: errorDetails.message,
      filename: errorDetails.filename || "unknown",
      lineno: errorDetails.lineno || 0,
      colno: errorDetails.colno || 0,
      stack: errorDetails.stack || "No stack trace available",
      iframeUrl: errorDetails.iframeUrl,
      userAgent: errorDetails.userAgent,
      timestamp: errorDetails.timestamp,
      error: true,
    });

    // Also track error patterns for analysis
    this.trackErrorPattern(errorDetails);
  }

  /**
   * Track error patterns for operational insights
   */
  private trackErrorPattern(errorDetails: IframeErrorDetails): void {
    // Extract error type from message or stack
    const errorType = this.extractErrorType(errorDetails);

    // Track error by type for pattern analysis
    trackMonitoringEvent("iframe_error_pattern", {
      errorType,
      iframeUrl: errorDetails.iframeUrl,
      timestamp: errorDetails.timestamp,
    });

    // Track specific course player errors (common in xAPI courses)
    if (this.isCoursePlayerError(errorDetails)) {
      trackMonitoringEvent("course_player_error", {
        message: errorDetails.message,
        iframeUrl: errorDetails.iframeUrl,
        timestamp: errorDetails.timestamp,
      });
    }
  }

  /**
   * Extract error type from error details
   */
  private extractErrorType(errorDetails: IframeErrorDetails): string {
    if (errorDetails.error?.name) {
      return errorDetails.error.name;
    }

    if (errorDetails.message.includes("TypeError")) return "TypeError";
    if (errorDetails.message.includes("ReferenceError")) return "ReferenceError";
    if (errorDetails.message.includes("SyntaxError")) return "SyntaxError";
    if (errorDetails.message.includes("NetworkError")) return "NetworkError";
    if (errorDetails.message.includes("JSON")) return "JSONError";

    return "UnknownError";
  }

  /**
   * Check if error is related to course player functionality
   */
  private isCoursePlayerError(errorDetails: IframeErrorDetails): boolean {
    const message = errorDetails.message.toLowerCase();
    const stack = errorDetails.stack?.toLowerCase() || "";

    return (
      message.includes("tincan") ||
      message.includes("xapi") ||
      message.includes("scorm") ||
      message.includes("course") ||
      stack.includes("tincan") ||
      stack.includes("xapi")
    );
  }

  /**
   * Stop monitoring and clean up listeners
   */
  public stopMonitoring(): void {
    if (this.iframeRef?.contentWindow && this.errorListener) {
      this.iframeRef.contentWindow.removeEventListener("error", this.errorListener, true);
    }

    if (this.iframeRef?.contentWindow && this.unhandledRejectionListener) {
      this.iframeRef.contentWindow.removeEventListener("unhandledrejection", this.unhandledRejectionListener, true);
    }

    this.iframeRef = null;
    this.errorListener = null;
    this.unhandledRejectionListener = null;
    this.isMonitoring = false;
  }

  /**
   * Test iframe access to verify same-origin configuration
   */
  public testIframeAccess(iframe: HTMLIFrameElement): boolean {
    try {
      // Try to access iframe content - this will fail if same-origin is not configured
      const testAccess = iframe.contentWindow?.location.href;
      console.log("Iframe access test successful");
      return true;
    } catch (error) {
      console.warn("Iframe access blocked", error);
      return false;
    }
  }
}

// Export singleton instance
export const iframeErrorMonitor = new IframeErrorMonitor();
